import {
	ActionButton,
	Button,
	DialogTrigger,
	Popover,
	TextField,
	ToggleButton,
} from '@react-spectrum/s2';
import Link from '@react-spectrum/s2/icons/Link';
import ListBulleted from '@react-spectrum/s2/icons/ListBulleted';
import ListNumbered from '@react-spectrum/s2/icons/ListNumbered';
import TextBold from '@react-spectrum/s2/icons/TextBold';
import TextItalic from '@react-spectrum/s2/icons/TextItalic';
import TextStrikeThrough from '@react-spectrum/s2/icons/TextStrikeThrough';
import { type Editor, useEditorState } from '@tiptap/react';
import { useState } from 'react';

import { cn } from '@/utils/cn.ts';

interface EmailEditorToolbarProps {
	editor: Editor;
	className?: string;
}

export const EmailEditorToolbar = ({
	editor,
	className,
}: EmailEditorToolbarProps) => {
	// TipTap v3 useEditor does not re-render on transactions; subscribe to the
	// active-mark flags so the toggle buttons reflect the current selection.
	const active = useEditorState({
		editor,
		selector: ({ editor: e }) => ({
			bold: e.isActive('bold'),
			italic: e.isActive('italic'),
			strike: e.isActive('strike'),
			bulletList: e.isActive('bulletList'),
			orderedList: e.isActive('orderedList'),
			link: e.isActive('link'),
		}),
	});

	return (
		<div
			className={cn(
				'flex shrink-0 items-center gap-1 border-b border-gray-200',
				className,
			)}
		>
			<ToggleButton
				isQuiet
				size="S"
				aria-label="Bold"
				isSelected={active.bold}
				onChange={() => editor.chain().focus().toggleBold().run()}
			>
				<TextBold />
			</ToggleButton>
			<ToggleButton
				isQuiet
				size="S"
				aria-label="Italic"
				isSelected={active.italic}
				onChange={() => editor.chain().focus().toggleItalic().run()}
			>
				<TextItalic />
			</ToggleButton>
			<ToggleButton
				isQuiet
				size="S"
				aria-label="Strikethrough"
				isSelected={active.strike}
				onChange={() => editor.chain().focus().toggleStrike().run()}
			>
				<TextStrikeThrough />
			</ToggleButton>
			<LinkButton editor={editor} isActive={active.link} />
			<ToggleButton
				isQuiet
				size="S"
				aria-label="Bullet list"
				isSelected={active.bulletList}
				onChange={() => editor.chain().focus().toggleBulletList().run()}
			>
				<ListBulleted />
			</ToggleButton>
			<ToggleButton
				isQuiet
				size="S"
				aria-label="Numbered list"
				isSelected={active.orderedList}
				onChange={() => editor.chain().focus().toggleOrderedList().run()}
			>
				<ListNumbered />
			</ToggleButton>
		</div>
	);
};

interface LinkButtonProps {
	editor: Editor;
	isActive: boolean;
}

const LinkButton = ({ editor, isActive }: LinkButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [href, setHref] = useState('');

	const applyLink = () => {
		const trimmed = href.trim();
		if (trimmed) {
			editor
				.chain()
				.focus()
				.extendMarkRange('link')
				.setLink({ href: trimmed })
				.run();
		} else {
			editor.chain().focus().extendMarkRange('link').unsetLink().run();
		}
		setIsOpen(false);
	};

	return (
		<DialogTrigger
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (open) {
					setHref(editor.getAttributes('link').href ?? '');
				}
				setIsOpen(open);
			}}
		>
			<ActionButton
				isQuiet
				size="S"
				aria-label="Add link"
				UNSAFE_className={isActive ? 'text-(--color-beacon-accent)' : undefined}
			>
				<Link />
			</ActionButton>
			<Popover placement="bottom start">
				<form
					className="flex items-end gap-2 p-3"
					onSubmit={(e) => {
						e.preventDefault();
						applyLink();
					}}
				>
					<TextField
						autoFocus
						label="Link URL"
						type="url"
						value={href}
						onChange={setHref}
						placeholder="https://example.com"
					/>
					<Button type="submit" variant="accent">
						Apply
					</Button>
				</form>
			</Popover>
		</DialogTrigger>
	);
};
